import { useState } from "react";
import { Space, Table, Input, message, ConfigProvider, theme, Skeleton, Breadcrumb } from "antd";
import {
  faDownload,
  faPen,
  faPlus,
  faTrashCan,
  faFolder,
  faFileZipper,
  faFilePdf,
  faFileImage,
  faCube,
  faFile,
  faFolderOpen,
  faChevronRight,
  faLink,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";

import AddFile from "./AddFile";
import AddFolder from "./AddFolder";
import EditFolder from "./EditFolder";
import Head from "../../Components/Head";
import styleFileLibrary from "./FileLibrary.module.css";
import { useDisclosure } from "../../hooks/useDisclosure";
import DeleteModal from "../../Components/Modal/DeleteModal";
import {
  useDeleteFile,
  useGetFileLibraryDetail,
  useGetFileLibraryList,
  useGetS3Browser,
} from "../../api/fileLibrary";
import { CONFIG } from "../../config";

// ⚠️ TEMPORARY — set to false before merging/deploying, this bypasses real API data
const SHOW_DUMMY_DATA = false;

const DUMMY_DB = {
  root: {
    name: "Root Library",
    parentId: null,
    sub_folders: [
      { id: 101, name: "Textures" },
      { id: 102, name: "360-Scenes" },
      { id: 103, name: "Documents" },
    ],
    model_3Ds: [
      { id: 204, file: "/media/dummy/user_manual.pdf" },
      { id: 205, file: "/media/dummy/avatar_model.glb" },
    ],
  },
  101: {
    name: "Textures",
    parentId: null,
    sub_folders: [
      { id: 1011, name: "Wood" },
      { id: 1012, name: "Metal" },
    ],
    model_3Ds: [
      { id: 201, file: "https://picsum.photos/id/10/200/200", name: "wood_texture.jpg" },
      { id: 202, file: "https://picsum.photos/id/20/200/200", name: "logo_transparent.png" },
    ],
  },
  1011: {
    name: "Wood",
    parentId: 101,
    sub_folders: [],
    model_3Ds: [
      { id: 301, file: "https://picsum.photos/id/30/200/200", name: "oak_base.jpg" },
      { id: 302, file: "https://picsum.photos/id/40/200/200", name: "pine_normal.png" },
    ],
  },
  1012: {
    name: "Metal",
    parentId: 101,
    sub_folders: [],
    model_3Ds: [
      { id: 303, file: "https://picsum.photos/id/50/200/200", name: "steel_diffuse.jpg" },
    ],
  },
  102: {
    name: "360-Scenes",
    parentId: null,
    sub_folders: [],
    model_3Ds: [
      { id: 401, file: "/media/dummy/office_360.zip" },
      { id: 402, file: "/media/dummy/lobby_360.zip" },
    ],
  },
  103: {
    name: "Documents",
    parentId: null,
    sub_folders: [],
    model_3Ds: [
      { id: 501, file: "/media/dummy/unknown_data.dat" },
    ],
  },
};

const getFileIcon = (filename) => {
  if (!filename) return faFile;
  const lower = filename.toLowerCase();
  if (lower.endsWith(".zip") || lower.endsWith(".rar") || lower.endsWith(".tar") || lower.endsWith(".gz")) return faFileZipper;
  if (lower.endsWith(".pdf")) return faFilePdf;
  if (lower.endsWith(".glb") || lower.endsWith(".gltf") || lower.endsWith(".obj") || lower.endsWith(".fbx")) return faCube;
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".png") || lower.endsWith(".gif") || lower.endsWith(".webp") || lower.endsWith(".svg")) return faFileImage;
  return faFile;
};

const isImageFile = (filename) => {
  if (!filename) return false;
  const lower = filename.toLowerCase();
  return lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".png") || lower.endsWith(".gif") || lower.endsWith(".webp") || lower.endsWith(".svg");
};

const getObjectKey = (filePath) => {
  if (!filePath) return "";
  if (!/^https?:\/\//i.test(filePath)) {
    return filePath.replace(/^\/+/, "");
  }
  try {
    const url = new URL(filePath);
    let key = url.pathname.replace(/^\/+/, "");
    const base = String(CONFIG.S3_BASE_URI || "").replace(/\/+$/, "");
    if (base) {
      const bucketPrefix = new URL(base).pathname.replace(/^\/+|\/+$/g, "");
      if (bucketPrefix && (key === bucketPrefix || key.startsWith(`${bucketPrefix}/`))) {
        key = key.slice(bucketPrefix.length).replace(/^\/+/, "");
      }
    }
    return key;
  } catch {
    return filePath.replace(/^\/+/, "");
  }
};

const getFilePublicUrl = (filePath) => {
  if (!filePath) return "";
  if (/^https?:\/\//i.test(filePath) && !/amazonaws\.com/i.test(filePath)) {
    return filePath;
  }
  const base = String(CONFIG.S3_BASE_URI || CONFIG.BASE_URI || "").replace(/\/+$/, "");
  const key = getObjectKey(filePath);
  if (!key) return base;
  return `${base}/${key}`;
};

const FileLibrary = () => {
  const { id: paramId } = useParams();
  const [searchParams] = useSearchParams();
  const s3Prefix = searchParams.get("prefix");
  const id = !SHOW_DUMMY_DATA ? s3Prefix : paramId;
  const navigate = useNavigate();
  const {
    isOpen: isOpenEdit,
    onOpen: onOpenEdit,
    onClose: onCloseEdit,
  } = useDisclosure();
  const {
    isOpen: isOpenAddFolder,
    onOpen: onOpenAddFolder,
    onClose: onCloseAddFolder,
  } = useDisclosure();
  const {
    isOpen: isOpenAddFile,
    onOpen: onOpenAddFile,
    onClose: onCloseAddFile,
  } = useDisclosure();
  const [showModal, setShowModal] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const { data, refetch, isLoading, isError, error } = useGetFileLibraryList();
  const { data: s3Data, isLoading: isLoadingS3, isError: isErrorS3, error: errorS3, refetch: refetchS3 } = useGetS3Browser(id || "");
  const {
    data: dataDetail,
    isLoading: isLoadingDetail,
    isError: isErrorDetail,
    error: errorDetail,
    refetch: refetchdt,
  } = useGetFileLibraryDetail(id);
  const { mutateAsync: mutateAsyncDelete } = useDeleteFile();

  const isS3Mode = !SHOW_DUMMY_DATA;

  const handleDeleteModel = () => {
    mutateAsyncDelete(deleteId).then((res) => {
      if (res.data) {
        setShowModal(false);
        if (id) {
          refetchdt();
        } else {
          refetch();
        }
      }
    });
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowModal((prev) => !prev);
  };

  const handleDownload = async (record) => {
    if (downloadingId) return;
    
    setDownloadingId(record.key);
    
    const downloadUrl = getFilePublicUrl(record.file);
    const filename = record.name || (record.file ? record.file.split('/').pop() : "download");

    try {
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error("Download failed:", error);
      message.error(`Download failed: ${error.message}`);
    } finally {
      setDownloadingId(null);
    }
  };


  const getBreadcrumbs = () => {
    if (SHOW_DUMMY_DATA) {
      const crumbs = [];
      let currId = id;
      let current = DUMMY_DB[currId] || DUMMY_DB.root;
      
      while (current) {
        crumbs.unshift({ id: currId, name: current.name });
        if (current.parentId !== null && current.parentId !== undefined) {
          currId = current.parentId;
          current = DUMMY_DB[currId];
        } else {
          // If we reached a node with no parent, but it's not root, prepend root
          if (currId !== null && currId !== undefined) {
            crumbs.unshift({ id: null, name: DUMMY_DB.root.name });
          }
          break;
        }
      }
      return crumbs;
      } else {
      // S3 Breadcrumb derived directly from prefix path
      const crumbs = [{ id: null, name: "Files" }];
      if (id) {
        // id is e.g. "textures/wood/"
        const parts = id.split('/').filter(p => p);
        let currentPrefix = "";
        for (const part of parts) {
          currentPrefix += part + "/";
          crumbs.push({ id: currentPrefix, name: part });
        }
      }
      return crumbs;
    }
  };

  const breadcrumbs = getBreadcrumbs();
  const activeData = SHOW_DUMMY_DATA ? (DUMMY_DB[id] || DUMMY_DB.root) : s3Data;
  const activeFolders = activeData?.sub_folders || [];
  const activeFiles = activeData?.model_3Ds || [];


  const renderContent = () => {
    const isFetchingData = !SHOW_DUMMY_DATA && isLoadingS3;

    if (isErrorS3 && !SHOW_DUMMY_DATA) {
      return <p className="text-red-500 mt-4">{errorS3?.message}</p>;
    }

    const tableData = [
      ...activeFolders.map(f => ({ ...f, itemType: 'folder', key: `folder-${f.id}` })),
      ...activeFiles.map(f => ({ ...f, itemType: 'file', key: `file-${f.id}` }))
    ].filter(item => {
      const name = item.name || (item.file ? item.file.split('/').pop() : "");
      return name.toLowerCase().includes(searchQuery.toLowerCase());
    });

    const loadingData = Array(5).fill(0).map((_, i) => ({
      key: `loading-${i}`,
      itemType: 'loading',
      name: '',
      size: 0,
      last_modified: '',
    }));

    const finalDataSource = isFetchingData ? loadingData : tableData;

    const formatSize = (bytes) => {
      if (bytes === 0) return '0 B';
      if (!bytes) return '-';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDate = (isoString) => {
      if (!isoString) return '-';
      const date = new Date(isoString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit'
      }).format(date);
    };

    const columns = [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (text, record) => {
          if (record.itemType === 'loading') {
            return (
              <div className="flex items-center gap-3">
                <Skeleton.Avatar active size="small" shape="square" />
                <Skeleton.Input active size="small" style={{ width: 200 }} />
              </div>
            );
          }
          const isFolder = record.itemType === 'folder';
          const displayName = text || (record.file ? record.file.split('/').pop() : "unknown");
          const icon = isFolder ? faFolder : getFileIcon(displayName);
          
          return (
            <div 
              className="flex items-center gap-3 cursor-pointer hover:underline text-blue-600"
              onClick={() => {
                if (isFolder) {
                  navigate(!SHOW_DUMMY_DATA ? `/web-twinprocms/file-library?prefix=${encodeURIComponent(record.id)}` : `/web-twinprocms/file-library/${encodeURIComponent(record.id)}`);
                } else {
                  window.open(getFilePublicUrl(record.file), "_blank");
                }
              }}
            >
              <FontAwesomeIcon icon={icon} className="text-xl text-[#3d4e65]" />
              <span className="font-semibold">{displayName}</span>
            </div>
          );
        },
      },
      {
        title: 'Type',
        key: 'type',
        render: (_, record) => {
          if (record.itemType === 'loading') return <Skeleton.Input active size="small" style={{ width: 60 }} />;
          return record.itemType === 'folder' ? 'Folder' : 'File';
        },
        width: 100,
      },
      {
        title: 'Size',
        dataIndex: 'size',
        key: 'size',
        render: (size, record) => {
          if (record.itemType === 'loading') return <Skeleton.Input active size="small" style={{ width: 80 }} />;
          return record.itemType === 'folder' ? '-' : formatSize(size);
        },
        width: 120,
      },
      {
        title: 'Last Modified',
        dataIndex: 'last_modified',
        key: 'last_modified',
        render: (date, record) => {
          if (record.itemType === 'loading') return <Skeleton.Input active size="small" style={{ width: 120 }} />;
          return record.itemType === 'folder' ? '-' : formatDate(date);
        },
        width: 200,
      },
        {
          title: 'Actions',
          key: 'actions',
          render: (_, record) => {
            if (record.itemType === 'loading') return <Skeleton.Button active size="small" shape="circle" />;
            
            let copyUrl = "";
            if (record.itemType === 'folder') {
              const path = !SHOW_DUMMY_DATA 
                ? `/web-twinprocms/file-library?prefix=${encodeURIComponent(record.id)}` 
                : `/web-twinprocms/file-library/${encodeURIComponent(record.id)}`;
              copyUrl = `${window.location.origin}${path}`;
            } else {
              copyUrl = getFilePublicUrl(record.file);
            }
            
            return (
              <div className="flex items-center gap-3">
                {record.itemType !== 'folder' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(record);
                    }}
                    disabled={downloadingId === record.key}
                    title="Download"
                    className="text-gray-400 hover:text-[#00f0ff] transition-colors disabled:opacity-50"
                  >
                    <FontAwesomeIcon icon={downloadingId === record.key ? faSpinner : faDownload} spin={downloadingId === record.key} className="text-lg" />
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(copyUrl);
                    message.success("Link copied to clipboard!");
                  }}
                  title="Copy Link"
                  className="text-gray-400 hover:text-[#00f0ff] transition-colors"
                >
                  <FontAwesomeIcon icon={faLink} className="text-lg" />
                </button>
              </div>
            );
          },
          width: 100,
        }
    ];

    const onSelectChange = (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
      selectedRowKeys,
      onChange: onSelectChange,
      getCheckboxProps: (record) => ({
        disabled: record.itemType === 'folder' || record.itemType === 'loading', // Disable folder selection for now since API doesn't support bulk folder delete
      }),
    };

    const handleBulkDelete = async () => {
      if (selectedRowKeys.length === 0) return;
      setIsDeletingBulk(true);
      
      const fileIdsToDelete = selectedRowKeys
        .filter(key => key.startsWith('file-'))
        .map(key => {
          const item = tableData.find(d => d.key === key);
          return item ? item.id : null;
        })
        .filter(id => id !== null);

      if (fileIdsToDelete.length === 0) {
        setIsDeletingBulk(false);
        return;
      }

      // Use Promise.allSettled to handle partial failures
      const results = await Promise.allSettled(
        fileIdsToDelete.map(id => mutateAsyncDelete(id))
      );

      const successfulIds = [];
      const failedCount = results.filter((result, index) => {
        if (result.status === 'fulfilled') {
          successfulIds.push(fileIdsToDelete[index]);
          return false;
        }
        return true;
      }).length;

      const successCount = successfulIds.length;

      // Keep failed items selected
      const failedKeys = selectedRowKeys.filter(key => {
        if (key.startsWith('folder-')) return true; // keep folders selected if they were
        const fileId = tableData.find(d => d.key === key)?.id;
        return !successfulIds.includes(fileId);
      });
      
      setSelectedRowKeys(failedKeys);

      if (failedCount > 0) {
        message.warning(`${successCount} of ${fileIdsToDelete.length} files deleted, ${failedCount} failed.`);
      } else {
        message.success(`Successfully deleted ${successCount} files.`);
      }

      if (id) {
        refetchdt();
      } else {
        refetch();
      }
      setIsDeletingBulk(false);
    };

    return (
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center bg-transparent">
          <ConfigProvider theme={{ algorithm: theme.darkAlgorithm, token: { colorBgContainer: 'rgba(5, 10, 31, 0.55)', colorBorder: 'rgba(0, 240, 255, 0.2)', colorPrimary: '#00f0ff' } }}>
            <Input.Search
              placeholder="Filter files and folders..."
              allowClear
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ width: 300 }}
              className={styleFileLibrary["search-input-override"]}
            />
          </ConfigProvider>
          {/* {selectedRowKeys.length > 0 && (
            <div className="flex items-center gap-4">
              <span className="font-medium text-gray-600">{selectedRowKeys.length} selected</span>
              <button
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded font-medium transition-colors disabled:opacity-50"
                onClick={handleBulkDelete}
                disabled={isDeletingBulk}
              >
                {isDeletingBulk ? 'Deleting...' : 'Delete Selected'}
              </button>
            </div>
          )} */}
        </div>
        
        <Table 
          rowSelection={rowSelection} 
          columns={columns} 
          dataSource={finalDataSource} 
          pagination={false}
          className="shadow-sm border rounded"
          locale={{
            emptyText: (
              <div className="flex flex-col items-center justify-center py-20 text-gray-500 opacity-60">
                <FontAwesomeIcon icon={faFolderOpen} className="text-6xl mb-4" />
                <p className="text-lg">This folder is empty.</p>
                <p className="text-sm">Upload files or create a subfolder to get started.</p>
              </div>
            )
          }}
        />
      </div>
    );
  };

  return (
    <main className={`${styleFileLibrary["file-library-main-container"]} cms-file-library flex flex-col gap-4`}>
      <Head title="File Library" />
      <div className={styleFileLibrary["file-library-title-btn-container"]}>
        <h1 className="text-2xl font-semibold m-0 text-white">File Library</h1>
        <div className={styleFileLibrary["file-library-btn-container"]}>
          {/* <button className={styleFileLibrary.edit} onClick={onOpenEdit}>
            <FontAwesomeIcon icon={faPen} /> Edit Folder
          </button> */}
          <button className={styleFileLibrary.add} onClick={onOpenAddFolder}>
            <FontAwesomeIcon icon={faPlus} /> Add Folder
          </button>
          <button className={styleFileLibrary["add-file"]} onClick={onOpenAddFile}>
            <FontAwesomeIcon icon={faPlus} /> Add File
          </button>
          <EditFolder
            name={activeData?.name}
            isOpen={isOpenEdit}
            onClose={onCloseEdit}
            id={id ? id : data?.id}
            refetch={isS3Mode ? refetchS3 : (id ? refetchdt : refetch)}
            mode={isS3Mode ? "s3" : "db"}
            prefix={id || ""}
          />
          <AddFolder
            isOpen={isOpenAddFolder}
            onClose={onCloseAddFolder}
            id={id ? id : data?.id}
            refetch={isS3Mode ? refetchS3 : (id ? refetchdt : refetch)}
            mode={isS3Mode ? "s3" : "db"}
            prefix={id || ""}
          />
          <AddFile
            isOpen={isOpenAddFile}
            onClose={onCloseAddFile}
            id={id ? id : data?.id}
            refetch={isS3Mode ? refetchS3 : (id ? refetchdt : refetch)}
            mode={isS3Mode ? "s3" : "db"}
            prefix={id || ""}
          />
        </div>
      </div>

      <div className="bg-transparent">
        <Breadcrumb separator="">
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            const path = !SHOW_DUMMY_DATA 
              ? `/web-twinprocms/file-library?prefix=${encodeURIComponent(crumb.id || '')}` 
              : `/web-twinprocms/file-library/${encodeURIComponent(crumb.id || '')}`;
            const copyUrl = crumb.id ? `${window.location.origin}${path}` : `${window.location.origin}/web-twinprocms/file-library`;

            return (
              <Breadcrumb.Item key={`crumb-${crumb.id || 'root'}`}>
                <span className="flex items-center inline-flex">
                  <span 
                    className={!isLast ? "cursor-pointer hover:text-[#00f0ff] transition-colors text-gray-400" : "font-medium text-gray-200"}
                    onClick={() => {
                      if (!isLast) {
                        navigate(crumb.id ? path : '/web-twinprocms/file-library');
                      }
                    }}
                  >
                    {crumb.name}
                  </span>
                  {!isLast && (
                    <Breadcrumb.Separator>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(copyUrl);
                          message.success("Link copied to clipboard!");
                        }}
                        title="Copy link to this folder"
                        className="text-gray-500 hover:text-[#00f0ff] transition-all cursor-pointer mx-1 px-1"
                      >
                        <FontAwesomeIcon icon={faChevronRight} className="text-[10px]" />
                      </button>
                    </Breadcrumb.Separator>
                  )}
                </span>
              </Breadcrumb.Item>
            );
          })}
        </Breadcrumb>
      </div>

      <div>
        {renderContent()}
        {showModal && (
          <DeleteModal onClose={setShowModal} onDelete={handleDeleteModel} />
        )}
      </div>
    </main>
  );
};

export default FileLibrary;
